/**
 * What the page embedding this application in a frame asks of it, read from the frame's own URL.
 *
 * The URL is the one channel available before the first paint, which is why the look travels
 * there and not in a message: a widget that starts in one brand and switches to another a moment
 * later is worse than one that is simply wrong. The language travels both ways: here for the first
 * screen, and in a message afterwards for when the reader changes it.
 */

/** This product's own colour, used when the embedding page asks for nothing. */
const DEFAULT_PRIMARY_COLOR = "#0084b8";

const PRIMARY_COLOR_PARAM = "primaryColor";
const FONT_SIZE_PARAM = "body";
const LANGUAGE_PARAM = "lang";

/** The colour the embedding page asked for, or this product's own. */
export function embedPrimaryColor(): string {
    const asked = parameter(PRIMARY_COLOR_PARAM);
    if (!asked) {
        return DEFAULT_PRIMARY_COLOR;
    }

    // A bare hex loses its `#` to the query string unless the page encoded it, and asking for a
    // colour the browser cannot read would stop the application at startup.
    const colour = /^[0-9a-fA-F]{3,8}$/.test(asked) ? `#${asked}` : asked;
    return typeof CSS !== "undefined" && CSS.supports && !CSS.supports("color", colour) ? DEFAULT_PRIMARY_COLOR : colour;
}

/**
 * The base text size the embedding page asked for, if it did.
 *
 * The page names its own variable, as `body=var(--website-body-size)`. That variable belongs to
 * the embedding page's document, not to this frame's, so it is resolved there: see `index.html`,
 * which reads it before building this frame's URL.
 */
export function embedFontSize(): string | null {
    return parameter(FONT_SIZE_PARAM);
}

/** The language the embedding page asked for, if it did. */
export function embedLanguage(): string | null {
    return parameter(LANGUAGE_PARAM);
}

function parameter(name: string): string | null {
    if (typeof window === "undefined") {
        return null;
    }
    const value = new URLSearchParams(window.location.search).get(name);
    return value && value.trim() !== "" ? value.trim() : null;
}
